import { z } from 'zod'

// Base API response schema
export const ApiResponseSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    code: z.number(),
    data: dataSchema,
    message: z.string()
  })

// API Error schema
export const ApiErrorSchema = z.object({
  code: z.number(),
  message: z.string()
})

export type ApiError = z.infer<typeof ApiErrorSchema>

// Auth API schemas
export const LoginRequestSchema = z.object({
  username: z.string(),
  password: z.string(),
  token_2fa: z.string().optional(),
  captcha_token: z.string().optional()
})

export type LoginRequest = z.infer<typeof LoginRequestSchema>

// User API schemas
export const UserInfoSchema = z.object({
  api_key: z.string(),
  email: z.string().email(),
  secret_key: z.string(),
  user_id: z.number(),
  user_name: z.string(),
  is_enabled_2fa: z.boolean()
})

export type UserInfo = z.infer<typeof UserInfoSchema>

// 2FA API schemas
export const Generate2FAResponseSchema = z.object({
  qr: z.string(),
  secret: z.string()
})

export type Generate2FAResponse = z.infer<typeof Generate2FAResponseSchema>

// Password change API schema
export const UpdatePasswordRequestSchema = z.object({
  user_id: z.number(),
  token: z.string(),
  password: z.string()
})

export type UpdatePasswordRequest = z.infer<typeof UpdatePasswordRequestSchema>


export const UpdateLoggedPasswordRequestSchema = z.object({
  old_password: z.string(),
  new_password: z.string()
})

export type UpdateLoggedPasswordRequest = z.infer<typeof UpdateLoggedPasswordRequestSchema>

// Wallet API schemas
export const PaymentSettingsSchema = z.object({
  payment_threshold: z.string(),
  wallet_address: z.string(),
  max_payment_threshold: z.number(),
  min_payment_threshold: z.number(),
  build_networks: z.array(z.enum(['mainnet', 'testnet', 'regtest']))
})

export type PaymentSettings = z.infer<typeof PaymentSettingsSchema>

export const PaymentHistoryItemSchema = z.object({
  amount: z.string(),
  timestamp: z.string(), // UTC date string
  tx: z.string(),
  wallet: z.string()
})

export const PaymentHistorySchema = z.object({
  data: z.array(PaymentHistoryItemSchema),
  page_num: z.number(),
  page_size: z.number(),
  total_page: z.number(),
  total_record: z.number()
})

export type PaymentHistoryItem = z.infer<typeof PaymentHistoryItemSchema>
export type PaymentHistory = z.infer<typeof PaymentHistorySchema>

// Stats API schemas
export const AccountOverviewSchema = z.object({
  active_worker_num: z.number(),
  estimated_block: z.number().optional(),
  estimated_window: z.number().optional(),
  hs_last_10m: z.number(), // API returns number in Mhash/s
  hs_last_1h: z.number(), // API returns number in Mhash/s
  hs_last_1d: z.number(), // API returns number in Mhash/s
  inactive_worker_num: z.number(),
  invalid_worker_num: z.number(),
  shares: z.number().optional(),
  total_amount: z.string(),
  total_worker_num: z.number(),
  unpaid_amount: z.string(),
  user_id: z.string(),
  yesterday_amount: z.string(),
  today_amount: z.string(), // NEW: today's payments
  connected: z.boolean()
})

export type AccountOverview = z.infer<typeof AccountOverviewSchema>

export const WorkerHashrateSchema = z.object({
  hs_last_10m: z.number(),
  hs_last_1h: z.number(),
  hs_last_1d: z.number(),
  last_active: z.number(),
  worker_name: z.string(),
  worker_status: z.string(),
  earnings: z.number().optional()
})

export const WorkersHashrateSchema = z.object({
  data: z.array(WorkerHashrateSchema),
  page_num: z.number(),
  page_size: z.number(),
  total_page: z.number(),
  total_record: z.number()
})

export type WorkerHashrate = z.infer<typeof WorkerHashrateSchema>
export type WorkersHashrate = z.infer<typeof WorkersHashrateSchema>

export const HashrateHistoryItemSchema = z.object({
  date: z.string(),
  hashrate: z.number(),
  reject_rate: z.number()
})

export const HashrateHistorySchema = z.object({
  data: z.array(HashrateHistoryItemSchema),
  page_num: z.number(),
  page_size: z.number(),
  total_page: z.number(),
  total_record: z.number()
})

export type HashrateHistoryItem = z.infer<typeof HashrateHistoryItemSchema>
export type HashrateHistory = z.infer<typeof HashrateHistorySchema>

export const HashrateChartItemSchema = z.object({
  hashrate: z.number(),
  reject_rate: z.number(),
  timestamp: z.number()
})

export const HashrateChartSchema = z.array(HashrateChartItemSchema)

export type HashrateChartItem = z.infer<typeof HashrateChartItemSchema>
export type HashrateChart = z.infer<typeof HashrateChartSchema>

// Worker Hashrate History Chart - API returns array directly
export const WorkerHashrateHistoryItemSchema = z.object({
  hashrate: z.number(),
  reject_rate: z.number(),
  timestamp: z.number()
})

export const WorkerHashrateHistorySchema = z.array(WorkerHashrateHistoryItemSchema)

export type WorkerHashrateHistoryItem = z.infer<typeof WorkerHashrateHistoryItemSchema>
export type WorkerHashrateHistory = z.infer<typeof WorkerHashrateHistorySchema>

// Common parameters
export interface PaginationParams {
  page?: number
  limit?: number
}

export interface DateRangeParams {
  start_date?: string
  end_date?: string
  range?: 'week' | 'month' | 'year'
}

export interface CoinParams {
  coin?: string
}

// Public Pool API schemas

export const PoolMetricsSchema = z.object({
  avg_block_time: z.number(),
  blocks_found: z.number(),
  hashrate_1d: z.number().optional(),
  hashrate_1h: z.number(),
  hashrate_now: z.number(),
  network_difficulty: z.number(),
  participiants: z.number(), // Note: API has typo "participiants" instead of "participants"
  share_log_window: z.number(),
  total_bitcoins_mined: z.number().optional(),
  total_mined: z.number().optional()
})

export type PoolMetrics = z.infer<typeof PoolMetricsSchema>

// Pool Hashrate Chart Item - API returns hashrate as number
export const PoolHashrateChartItemSchema = z.object({
  hashrate: z.number(), // API returns number in Mhash/s
  timestamp: z.number()
})

export const PoolHashrateChartSchema = z.array(PoolHashrateChartItemSchema)

export type PoolHashrateChartItem = z.infer<typeof PoolHashrateChartItemSchema>
export type PoolHashrateChart = z.infer<typeof PoolHashrateChartSchema>

export const FoundBlockSchema = z.object({
  hash: z.string(),
  height: z.number(),
  shares: z.number(),
  solver: z.string(),
  timestamp: z.number()
})

// Found blocks pagination wrapper
export const FoundBlocksPaginationSchema = z.object({
  data: z.array(FoundBlockSchema),
  page_num: z.number(),
  page_size: z.number(),
  total_page: z.number(),
  total_record: z.number()
})

export type FoundBlock = z.infer<typeof FoundBlockSchema>
export type FoundBlocksPagination = z.infer<typeof FoundBlocksPaginationSchema>
export type FoundBlocks = z.infer<typeof FoundBlocksPaginationSchema>

// Users (Contributors) API schemas

export const UserContributorSchema = z.object({
  hashrate: z.number(),
  hashrate_percent: z.number().nullable(),
  name: z.string(),
  num: z.number(),
  shares_percent: z.number().nullable()
})

export const UsersListResponseSchema = z.object({
  data: z.array(UserContributorSchema),
  page_num: z.number(),
  page_size: z.number(),
  total_page: z.number(),
  total_record: z.number()
})

export const UserSearchResultSchema = z.object({
  name: z.string(),
  rank: z.number(),
  hashrate: z.number(),
  shares_percent: z.number().nullable(),
  hashrate_percent: z.number().nullable()
})

export const UsersSearchResponseSchema = z.object({
  users: z.array(UserSearchResultSchema),
  page: z.number(),
  pages: z.number(),
  limit: z.number(),
  total: z.number()
})

export type UserContributor = z.infer<typeof UserContributorSchema>
export type UsersListResponse = z.infer<typeof UsersListResponseSchema>
export type UserSearchResult = z.infer<typeof UserSearchResultSchema>
export type UsersSearchResponse = z.infer<typeof UsersSearchResponseSchema>

// Unreg User Account Overview schema
export const UnregAccountOverviewSchema = z.object({
  active_worker_num: z.number(),
  connected: z.boolean(), // NEW: connection status
  estimated_block: z.number(),
  estimated_window: z.number(),
  hs_last_10m: z.number(), // API returns number in Mhash/s
  hs_last_1h: z.number(), // API returns number in Mhash/s
  hs_last_1d: z.number(), // API returns number in Mhash/s
  inactive_worker_num: z.number(),
  invalid_worker_num: z.number(),
  shares: z.number(),
  total_amount: z.string(),
  total_worker_num: z.number(),
  unpaid_amount: z.string(),
  user_id: z.string(),
  yesterday_amount: z.string()
})

export type UnregAccountOverview = z.infer<typeof UnregAccountOverviewSchema>

// Unreg Workers List schema
export const UnregWorkersListSchema = z.array(z.string())

export type UnregWorkersList = z.infer<typeof UnregWorkersListSchema>

// Worker Overview schema
export const WorkerOverviewSchema = z.object({
  estimated_block: z.number(),
  estimated_window: z.number(),
  hs_last_10m: z.number(),
  hs_last_1d: z.number(),
  hs_last_1h: z.number(),
  shares: z.number(),
  worker_id: z.string()
})

export type WorkerOverview = z.infer<typeof WorkerOverviewSchema>

// Reward History schemas

export const RewardHistoryItemSchema = z.object({
  amount: z.string(),
  timestamp: z.string()
})

export const RewardHistorySchema = z.object({
  data: z.array(RewardHistoryItemSchema),
  page_num: z.number(),
  page_size: z.number(),
  total_page: z.number(),
  total_record: z.number()
})

export type RewardHistoryItem = z.infer<typeof RewardHistoryItemSchema>
export type RewardHistory = z.infer<typeof RewardHistorySchema>

// Workers Stats schemas (different from WorkersHashrate)
export const WorkerStatsSchema = z.object({
  earnings: z.number(),
  hashrate_10m: z.number(),
  hashrate_1d: z.number(),
  hashrate_1h: z.number(),
  last_share_time: z.string(),
  status: z.string(),
  worker_name: z.string()
})

export const WorkersStatsSchema = z.object({
  page: z.number(),
  page_size: z.number(),
  total_pages: z.number(),
  total_records: z.number(),
  workers: z.array(WorkerStatsSchema)
})

export type WorkerStats = z.infer<typeof WorkerStatsSchema>
export type WorkersStats = z.infer<typeof WorkersStatsSchema>

// ===== CRYPTO API TYPES =====

export const CryptoPriceSchema = z.object({
  bitcoin: z.object({
    usd: z.number(),
    usd_market_cap: z.number().optional(),
    usd_24h_change: z.number().optional(),
    last_updated_at: z.number().optional()
  })
})

export const CryptoSimplePriceResponseSchema = z.record(
  z.string(), // coin id (e.g., "bitcoin")
  z.record(z.string(), z.number()) // currency -> price mapping
)

export type CryptoPrice = z.infer<typeof CryptoPriceSchema>
export type CryptoSimplePriceResponse = z.infer<typeof CryptoSimplePriceResponseSchema>


export const CryptoMarketDataSchema = z.object({
  id: z.string(),
  symbol: z.string(),
  name: z.string(),
  current_price: z.number(),
  market_cap: z.number().optional(),
  price_change_24h: z.number().optional(),
  price_change_percentage_24h: z.number().optional(),
  last_updated: z.string().optional()
})

export type CryptoMarketData = z.infer<typeof CryptoMarketDataSchema>
